import { useState } from 'react';
import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import type { FolderContentApi } from '../types';
import { confirmDialog } from '../../ui/feedback';

interface UseFileSelectionArgs {
  files: Array<any>;
  unfilteredFiles: Array<any>;
  folderContent: FolderContentApi;
  showBlockingLoader: boolean;
  setFiles: Dispatch<SetStateAction<Array<any>>>;
  setUnfilteredFiles: Dispatch<SetStateAction<Array<any>>>;
  setTotalFiles: Dispatch<SetStateAction<number>>;
  setFolderError: Dispatch<SetStateAction<string>>;
  setFolderSuccess: Dispatch<SetStateAction<string>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  updatePermissionList: (items: Array<any>) => void;
  getInitialFiles: () => Promise<void>;
  getTotalFiles: () => Promise<void>;
}

export const getAvailablePermissions = (items: Array<any>, groups: Array<any>) => {
  const newPermissions: Array<any> = [];

  Array.isArray(items) &&
    items.length > 0 &&
    items.forEach((file) => {
      const permissions = JSON.parse(file.access_level);
      permissions.forEach((permission: any) => {
        if (
          permission > 1 &&
          !newPermissions.some((item) => item.ID === permission)
        ) {
          const obj = groups.find((group) => group.ID === permission);
          if (obj) {
            newPermissions.push(obj);
          }
        }
      });
    });

  return newPermissions.sort((a, b) => a.name.localeCompare(b.name));
};

const removeFilesById = (currentFiles: Array<any>, deletedIds: Array<number>) =>
  currentFiles.filter((file) => !deletedIds.includes(file.ID));

export const useFileSelection = ({
  files,
  unfilteredFiles,
  folderContent,
  showBlockingLoader,
  setFiles,
  setUnfilteredFiles,
  setTotalFiles,
  setFolderError,
  setFolderSuccess,
  setLoading,
  updatePermissionList,
  getInitialFiles,
  getTotalFiles,
}: UseFileSelectionArgs) => {
  const [selectedItems, setSelectedItems] = useState<Array<number>>([]);
  const [allSelected, setAllSelected] = useState(false);

  const selectItem = (id: number) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const selectAll = () => {
    if (allSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(files.map((file) => file.ID));
    }
    setAllSelected(!allSelected);
  };

  const filterByGroup = (e: ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === 'all') {
      setFiles(unfilteredFiles);
      return;
    }

    const groupID = parseInt(e.target.value);
    const newFiles = unfilteredFiles.filter((file) => {
      const permissions = JSON.parse(file.access_level);

      return permissions.includes(groupID);
    });

    setFiles(newFiles);
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setAllSelected(false);
  };

  const deleteSelected = async () => {
    if (selectedItems.length === 0 || showBlockingLoader) {
      return;
    }

    if (
      !(await confirmDialog({
        title: 'Delete selected files?',
        message: 'Are you sure you want to delete the selected files?',
        confirmLabel: 'Delete',
        variant: 'danger',
      }))
    ) {
      return;
    }

    setLoading(true);

    try {
      const itemsToDelete = [...selectedItems];
      const responses = await Promise.all(
        itemsToDelete.map(async (selectedItem) => {
          try {
            const data = await folderContent.moveToTrash(selectedItem);

            if (data === 'Error decoding token: Expired token') {
              return {
                id: selectedItem,
                data: {
                  error: 'Your session has expired.',
                },
              };
            }

            return {
              id: selectedItem,
              data,
            };
          } catch (error) {
            return {
              id: selectedItem,
              data: {
                error:
                  error instanceof Error
                    ? error.message
                    : 'Could not delete file.',
              },
            };
          }
        })
      );

      const failedResponses = responses.filter(({ data }) => data?.error);
      const deletedIds = responses
        .filter(({ data }) => !data?.error)
        .map(({ id }) => id);

      if (failedResponses.length > 0) {
        setFolderError(failedResponses.map(({ data }) => data.error).join(' '));
      } else {
        setFolderError('');
      }

      if (deletedIds.length > 0) {
        setFiles((currentFiles) => {
          const nextFiles = removeFilesById(currentFiles, deletedIds);
          updatePermissionList(nextFiles);
          return nextFiles;
        });
        setUnfilteredFiles((currentFiles) =>
          removeFilesById(currentFiles, deletedIds)
        );
        setTotalFiles((currentTotal) =>
          Math.max(0, currentTotal - deletedIds.length)
        );
        setFolderSuccess(
          deletedIds.length === 1
            ? 'File moved to trash.'
            : `${deletedIds.length} files moved to trash.`
        );
        await getInitialFiles();
        await getTotalFiles();
      }

      clearSelection();
      setLoading(false);
    } catch (error) {
      console.error('Error deleting files:', error);
      setLoading(false);
    }
  };

  return {
    selectedItems,
    allSelected,
    setSelectedItems,
    setAllSelected,
    clearSelection,
    selectItem,
    selectAll,
    filterByGroup,
    deleteSelected,
  };
};
